import { ROUTES } from 'app/utils/constants/paths';
import { LOCALSTORAGES } from 'core/variables/localStorages';

type LoadedUser = { guid: string; referrer?: unknown };

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

const SSO_CALLBACK_ROUTES: string[] = [ROUTES.SAML_CALLBACK, ROUTES.OIDC_CALLBACK];
const TOKEN_HANDOFF_ROUTES: string[] = [...SSO_CALLBACK_ROUTES, ROUTES.AUTH_LOGIN_TOKEN];

// El token es el segmento que sigue al prefijo; lo que venga detrás es el deep link que resuelve
// redirectFromLoginWithToken. `indexOf` y no `startsWith` para que valga bajo un `basename`.
function getRouteToken(pathname: string, routes: string[]): string | null {
  for (const route of routes) {
    const prefix = route.slice(0, route.indexOf(':token'));
    const prefixIndex = pathname.indexOf(prefix);
    if (prefixIndex !== -1) {
      const [token] = pathname.slice(prefixIndex + prefix.length).split('/');
      return token && `${prefix}${token}` !== ROUTES.SAML_ERROR ? token : null;
    }
  }
  return null;
}

/**
 * Token de la URL de relevo de sesión (`/saml|oidc/callback/:token`, `/auth/login/:token`): mientras
 * se está en ella, la sesión vigente es la de ese token aunque el store aún tenga la persistida.
 */
export function getHandoffToken(pathname: string): string | null {
  return getRouteToken(pathname, TOKEN_HANDOFF_ROUTES);
}

/** Callback del portal SSO con token (no su página de error ni `/auth/login/:token`). */
export function isSsoCallbackPath(pathname: string): boolean {
  return getRouteToken(pathname, SSO_CALLBACK_ROUTES) !== null;
}

function toAbsoluteHttpUrl(value: string): string | null {
  try {
    return ALLOWED_PROTOCOLS.has(new URL(value).protocol) ? value : null;
  } catch {
    return null;
  }
}

// Solo un usuario con guid está cargado: tras el login, auth.user es undefined hasta que llega
// el perfil, y sin él no se sabe si la sesión actual es SSO.
function isLoadedUser(user: unknown): user is LoadedUser {
  return typeof user === 'object' && user !== null && Boolean((user as LoadedUser).guid);
}

/**
 * Devuelve la URL del portal SSO si `raw` es una URL absoluta http(s), en claro o codificada con
 * encodeURIComponent (Logout decodificaba `user.referrer` y el 403 lo usaba en claro: se aceptan las
 * dos formas). Una URL en claro no se decodifica, para no alterar los `%` de su query.
 */
export function normalizeSsoPortalUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  const plainUrl = toAbsoluteHttpUrl(trimmed);
  if (plainUrl) return plainUrl;

  try {
    return toAbsoluteHttpUrl(decodeURIComponent(trimmed).trim());
  } catch {
    return null;
  }
}

/**
 * Olvida el portal recordado. Un login que no llega por el callback del portal SSO puede ser de otro
 * usuario: si este también es SSO, UserLoaded vuelve a guardar el suyo.
 */
export function forgetSsoPortal(): void {
  try {
    localStorage.removeItem(LOCALSTORAGES.SSO_PORTAL_FALLBACK);
  } catch {
    // Sin localStorage tampoco se pudo recordar ningún portal.
  }
}

/**
 * Guarda fuera del store persistido el portal del usuario cargado, para que /logout pueda volver a
 * él cuando otra pestaña o un cierre anterior ya ha borrado la sesión (TAN-11526).
 */
export function rememberSsoPortal(user: unknown): void {
  if (!isLoadedUser(user)) return;

  const portalUrl = normalizeSsoPortalUrl(user.referrer);
  if (!portalUrl) {
    forgetSsoPortal();
    return;
  }

  try {
    // setItem directo, no setStorage: este añade una caducidad de un día que no aplica aquí.
    localStorage.setItem(LOCALSTORAGES.SSO_PORTAL_FALLBACK, portalUrl);
  } catch {
    // Sin localStorage (modo privado, WebView) no hay respaldo: /logout irá al login.
  }
}

/**
 * Portal SSO al que volver al perder la sesión. Con un usuario cargado manda su referrer y nunca
 * el respaldo guardado, que puede ser de un usuario SSO anterior.
 */
export function getSsoPortalUrl(user: unknown): string | null {
  if (isLoadedUser(user)) return normalizeSsoPortalUrl(user.referrer);

  try {
    return normalizeSsoPortalUrl(localStorage.getItem(LOCALSTORAGES.SSO_PORTAL_FALLBACK));
  } catch {
    return null;
  }
}
